import {
  AlertTriangle,
  Check,
  PackageCheck,
  RefreshCcw,
  Send,
  X
} from "lucide-react";
import type { OrderConfirmationChoice } from "../../domain/order-confirmation";
import type { OrderConfirmationViewState } from "./useOrderConfirmationWorkspace";
const choices: Array<{ value: OrderConfirmationChoice; label: string; Icon: typeof Check; }> = [
  { value: "ACCEPTED", label: "Kabul et", Icon: Check },
  { value: "CHANGE_REQUESTED", label: "Değişiklik iste", Icon: Send },
  { value: "REJECTED", label: "Reddet", Icon: X },
];
const money = (value: number, currency: string) => new Intl.NumberFormat("tr-TR", { style: "currency", currency }).format(value);
export function OrderConfirmationView({
  workspace,
  canRetryRead,
  loading,
  error,
  choice,
  setChoice,
  reason,
  setReason,
  date,
  setDate,
  quantity,
  setQuantity,
  note,
  setNote,
  submitting,
  done,
  valid,
  locked,
  uncertain,
  today,
  submit,
  load
}: OrderConfirmationViewState) {
  if (loading)
    return (
      <main className="order-confirmation-portal">
        <section className="order-confirmation-loading">
          <RefreshCcw className="spin" />
          <strong>Sipariş güvenli biçimde açılıyor…</strong>
        </section>
      </main>
    );
  if (error && !workspace)
    return (
      <main className="order-confirmation-portal">
        <section className="order-confirmation-error">
          <AlertTriangle />
          <h1>Bağlantı açılamadı</h1>
          <p>{error}</p>
          {canRetryRead ? <button className="order-confirmation-submit" onClick={() => void load()}>Yeniden dene</button> : <p>Satın Alma ekibinden yeni bir sipariş bağlantısı isteyin.</p>}
        </section>
      </main>
    );
  if (!workspace) return null;
  const order = workspace.order;
  if (done)
    return (
      <main className="order-confirmation-portal">
        <section className="order-confirmation-success">
          <Check />
          <span>YANITINIZ KAYDEDİLDİ</span>
          <h1>{order.orderNo}</h1>
          <p>
            Yanıtınız ANL Satın Alma ekibine iletildi. Bu pencereyi güvenle
            kapatabilirsiniz.
          </p>
        </section>
      </main>
    );
  return (
    <main className="order-confirmation-portal">
      <header>
        <div>
          <span>ANL TEDARİK</span>
          <strong>Sipariş teyidi</strong>
        </div>
        <small>Güvenli tedarikçi alanı</small>
      </header>
      <div className="order-confirmation-layout">
        <section className="order-confirmation-card">
          <div className="order-confirmation-title">
            <span>SİPARİŞ</span>
            <h1>{order.orderNo}</h1>
            <p>
              {order.supplierName} · {order.requestTitle}
            </p>
          </div>
          <dl>
            <div>
              <dt>Miktar</dt>
              <dd>
                {order.quantity} {order.unit}
              </dd>
            </div>
            <div>
              <dt>Toplam</dt>
              <dd>{money(order.totalAmount, order.currency)}</dd>
            </div>
            <div>
              <dt>Ödeme</dt>
              <dd>{order.paymentDays} gün</dd>
            </div>
            <div>
              <dt>Beklenen teslimat</dt>
              <dd>
                {new Date(`${order.expectedDeliveryAt}T00:00:00Z`).toLocaleDateString("tr-TR", { timeZone: "UTC" })}
              </dd>
            </div>
          </dl>
          <address>
            <span>Teslimat adresi</span>
            <strong>{order.deliveryAddress ?? "Doğrulanmadı"}</strong>
          </address>
        </section>
        <section className="order-confirmation-form">
          <div>
            <span>YANITINIZ</span>
            <h2>Siparişi değerlendirin</h2>
            <p>
              Kabul durumunda miktarın tamamını kapsayan teslimat planını
              bildirin.
            </p>
          </div>
          <div className="order-confirmation-choices">
            {choices.map(({ value, label, Icon }) => (
              <button
                key={value}
                type="button"
                className={choice === value ? "selected" : ""}
                disabled={locked}
                onClick={() => setChoice(value)}
              >
                <Icon size={18} />
                <span>{label}</span>
              </button>
            ))}
          </div>
          {choice === "ACCEPTED" ? (
            <div className="order-confirmation-fields">
              <label>
                Planlanan teslimat tarihi
                <input
                  type="date"
                  min={today}
                  disabled={locked}
                  value={date}
                  onChange={(event) => setDate(event.target.value)}
                />
              </label>
              <label>
                Teslim edilecek miktar
                <input
                  type="number"
                  min="0.001"
                  step="0.001"
                  disabled={locked}
                  value={quantity}
                  onChange={(event) => setQuantity(event.target.value)}
                  placeholder={`${order.quantity} ${order.unit}`}
                />
                <small>
                  Toplam sipariş miktarı: {order.quantity} {order.unit}
                </small>
              </label>
              <label>
                Teslimat notu
                <textarea
                  rows={3}
                  disabled={locked}
                  value={note}
                  onChange={(event) => setNote(event.target.value)}
                  placeholder="Varsa sevkiyat veya teslimat notunuzu yazın"
                />
              </label>
            </div>
          ) : (
            <label className="order-confirmation-reason">
              {choice === "REJECTED" ? "Ret gerekçesi" : "İstenen değişiklik"}
              <textarea
                rows={5}
                disabled={locked}
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="En az 10 karakterle açıkça yazın"
              />
              <small>{reason.trim().length}/10</small>
            </label>
          )}
          {error ? (
            <div className="order-confirmation-inline-error" role="alert">
              <AlertTriangle size={17} />
              {error}
              {!uncertain ? <button type="button" className="secondary-button" onClick={() => void load()}>Siparişi yenile</button> : null}
            </div>
          ) : null}
          <button
            className="order-confirmation-submit"
            disabled={submitting || (!uncertain && !valid)}
            onClick={() => void submit()}
          >
            <PackageCheck size={18} />
            {submitting ? "Kaydediliyor…" : uncertain ? "Aynı yanıtı tekrar gönder" : "Yanıtı güvenli biçimde gönder"}
          </button>
        </section>
      </div>
    </main>
  );
}
